import {
  ArrowLeft,
  Check,
  KeyRound,
  Mail,
  MailCheck,
  Pencil,
  RefreshCw,
  ShieldCheck,
  UserCog,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Container } from '../../components/ui/Container';
import { PageHero } from '../../components/ui/PageHero';
import { useAuth } from '../../lib/auth';
import { pb } from '../../lib/pb';
import { useDialog } from '../../lib/useDialog';
import { usePageMeta } from '../../lib/usePageMeta';

interface UserRow {
  id: string;
  email: string;
  name: string;
  unit_number: string | null;
  role: 'owner' | 'admin' | 'super_admin';
  must_change_password: boolean;
}

const ROLE_RANK: Record<UserRow['role'], number> = {
  super_admin: 0,
  admin: 1,
  owner: 2,
};

const ROLE_LABEL: Record<UserRow['role'], string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
  owner: 'Owner',
};

const ROLE_CLASSES: Record<UserRow['role'], string> = {
  super_admin: 'bg-brand-charcoal text-brand-yellow',
  admin: 'bg-brand-yellow text-brand-charcoal',
  owner: 'bg-brand-surface text-brand-body-dark',
};

export function AdminUsers(): JSX.Element {
  usePageMeta();
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [sendingLinkId, setSendingLinkId] = useState<string | null>(null);
  const [editingEmailId, setEditingEmailId] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [savingEmailId, setSavingEmailId] = useState<string | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [savingNameId, setSavingNameId] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const { confirm, alert, dialog } = useDialog();

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      // Primary source: the admin roster endpoint. The users collection keeps
      // emailVisibility=false, so a plain collection list returns blank emails
      // for everyone but the caller. The roster hook returns the real current
      // email to admins so they can spot placeholder condoNNN@test.com owners.
      const result = await pb.send<{ items: UserRow[] }>('/api/admin/users/roster', {
        method: 'GET',
      });
      setUsers(result.items);
    } catch (rosterErr) {
      // Fallback: keep the page working (emails may be blank) if the roster
      // hook is unavailable for any reason.
      console.error(rosterErr);
      try {
        const result = await pb.collection('users').getList<UserRow>(1, 200, {
          sort: 'role,unit_number',
          fields: 'id,email,name,unit_number,role,must_change_password',
        });
        setUsers(result.items);
      } catch (listErr) {
        console.error(listErr);
        setError('Could not load user accounts.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!users) return null;
    const q = search.trim().toLowerCase();
    const matches = q
      ? users.filter(
          (u) =>
            u.email.toLowerCase().includes(q) ||
            (u.name ?? '').toLowerCase().includes(q) ||
            (u.unit_number ?? '').toLowerCase().includes(q),
        )
      : users;
    return [...matches].sort((a, b) => {
      const r = ROLE_RANK[a.role] - ROLE_RANK[b.role];
      if (r !== 0) return r;
      return (a.unit_number ?? '').localeCompare(b.unit_number ?? '');
    });
  }, [users, search]);

  const canResetTarget = (target: UserRow): boolean => {
    if (!currentUser) return false;
    if (target.id === currentUser.id) return false;
    if (currentUser.role === 'admin' && target.role === 'super_admin') return false;
    return true;
  };

  async function onReset(target: UserRow): Promise<void> {
    // PocketBase hides other users' emails from non-superuser queries
    // (emailVisibility=false on the users collection), so we identify
    // the target by name + unit number rather than email.
    const displayName = target.name?.trim() || 'this account';
    const unitDetail = target.unit_number ? ` (Unit ${target.unit_number})` : '';
    const ok = await confirm(
      `Reset the password for ${displayName}${unitDetail}? They will get an email with the temporary password and be required to set a new one on next sign-in.`,
    );
    if (!ok) return;
    setResettingId(target.id);
    setFlash(null);
    try {
      await pb.send(`/api/admin/users/${target.id}/reset-password`, { method: 'POST' });
      setFlash(
        `Password reset for ${displayName}${unitDetail}. A notification email has been sent with the temporary password and sign-in instructions.`,
      );
      await load();
    } catch (err) {
      console.error(err);
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Reset failed. Try again.';
      await alert(msg);
    } finally {
      setResettingId(null);
    }
  }

  async function onSendResetLink(target: UserRow): Promise<void> {
    const displayName = target.name?.trim() || 'this account';
    const unitDetail = target.unit_number ? ` (Unit ${target.unit_number})` : '';
    const ok = await confirm(
      `Email a password reset link to ${displayName}${unitDetail}? They will click the link in the email and pick a new password directly. No temp password to type.`,
    );
    if (!ok) return;
    setSendingLinkId(target.id);
    setFlash(null);
    try {
      await pb.send(`/api/admin/users/${target.id}/send-reset-link`, { method: 'POST' });
      setFlash(
        `Reset link sent to ${displayName}${unitDetail}. The link is valid for 30 minutes.`,
      );
    } catch (err) {
      console.error(err);
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Send failed. Try again.';
      await alert(msg);
    } finally {
      setSendingLinkId(null);
    }
  }

  function startEditEmail(target: UserRow): void {
    // Only one edit at a time — cancel any active name edit on this or any row.
    setEditingNameId(null);
    setNameDraft('');
    setEditingEmailId(target.id);
    setEmailDraft(target.email ?? '');
    setFlash(null);
  }

  function cancelEditEmail(): void {
    setEditingEmailId(null);
    setEmailDraft('');
  }

  function startEditName(target: UserRow): void {
    // Mutually exclusive with email edit.
    setEditingEmailId(null);
    setEmailDraft('');
    setEditingNameId(target.id);
    setNameDraft(target.name ?? '');
    setFlash(null);
  }

  function cancelEditName(): void {
    setEditingNameId(null);
    setNameDraft('');
  }

  async function onSaveName(target: UserRow): Promise<void> {
    const next = nameDraft.replace(/\s+/g, ' ').trim();
    if (!next) {
      await alert('Enter a name.');
      return;
    }
    if (next.length > 200) {
      await alert('Name is too long (max 200 characters).');
      return;
    }
    if (next === (target.name ?? '').trim()) {
      cancelEditName();
      return;
    }
    const unitDetail = target.unit_number ? ` (Unit ${target.unit_number})` : '';
    const oldName = target.name?.trim() || '(no name)';
    const ok = await confirm(
      `Change the display name for${unitDetail ? unitDetail : ' this account'} from "${oldName}" to "${next}"? Their email, password, and role are not changed.`,
    );
    if (!ok) return;
    setSavingNameId(target.id);
    setFlash(null);
    try {
      await pb.send(`/api/admin/users/${target.id}/name`, {
        method: 'POST',
        body: { name: next },
      });
      setFlash(`Name for${unitDetail} updated to "${next}".`);
      cancelEditName();
      await load();
    } catch (err) {
      console.error(err);
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Could not update the name. Try again.';
      await alert(msg);
    } finally {
      setSavingNameId(null);
    }
  }

  async function onSaveEmail(target: UserRow): Promise<void> {
    const next = emailDraft.trim().toLowerCase();
    if (!next) {
      await alert('Enter an email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      await alert('That does not look like a valid email address.');
      return;
    }
    if (next === (target.email ?? '').toLowerCase()) {
      cancelEditEmail();
      return;
    }
    const displayName = target.name?.trim() || 'this account';
    const unitDetail = target.unit_number ? ` (Unit ${target.unit_number})` : '';
    const ok = await confirm(
      `Change the email for ${displayName}${unitDetail} to ${next}? They will use this address to sign in and to receive portal emails. Their password is not changed.`,
    );
    if (!ok) return;
    setSavingEmailId(target.id);
    setFlash(null);
    try {
      await pb.send(`/api/admin/users/${target.id}/email`, {
        method: 'POST',
        body: { email: next },
      });
      setFlash(`Email for ${displayName}${unitDetail} updated to ${next}.`);
      cancelEditEmail();
      await load();
    } catch (err) {
      console.error(err);
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Could not update the email. Try again.';
      await alert(msg);
    } finally {
      setSavingEmailId(null);
    }
  }

  return (
    <>
      {dialog}
      <PageHero
        eyebrow="Admin"
        title="Owner accounts"
        lead="Look up an owner or board member, update their display name or email address, or reset their password if they get locked out. Resets force the account to choose a new password on the next sign-in. Owners cannot reach this page."
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-charcoal underline decoration-brand-yellow-deep/40 underline-offset-4 hover:decoration-brand-yellow-deep"
            >
              <ArrowLeft size={14} aria-hidden="true" />
              Back to portal
            </Link>
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="inline-flex h-10 items-center gap-2 rounded-md border border-brand-surface bg-brand-white px-4 text-sm font-semibold text-brand-charcoal hover:bg-brand-surface/40 disabled:opacity-60"
            >
              <RefreshCw size={14} aria-hidden="true" className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          <div className="mb-6">
            <label className="block max-w-md">
              <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                Search by email, name, or unit
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="e.g., 207 or sadowski"
                className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-sm text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
              />
            </label>
          </div>

          {flash ? (
            <div
              role="status"
              className="mb-6 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
            >
              {flash}
            </div>
          ) : null}

          {error ? (
            <div
              role="alert"
              className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {error}
            </div>
          ) : null}

          {filtered === null ? (
            <p className="text-sm text-brand-body-medium">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-brand-body-medium">No accounts match that search.</p>
          ) : (
            <ul className="divide-y divide-brand-surface rounded-lg border border-brand-surface bg-brand-white">
              {filtered.map((u) => {
                const isSelf = currentUser?.id === u.id;
                const allowed = canResetTarget(u);
                const resetting = resettingId === u.id;
                const sendingLink = sendingLinkId === u.id;
                const editingEmail = editingEmailId === u.id;
                const savingEmail = savingEmailId === u.id;
                const editingName = editingNameId === u.id;
                const savingName = savingNameId === u.id;
                const editingAnywhere = editingEmail || editingName;
                const isPlaceholderEmail = /@test\.com$/i.test(u.email ?? '');
                const anyActionInFlight = resetting || sendingLink || savingEmail || savingName;
                return (
                  <li key={u.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold ${ROLE_CLASSES[u.role]}`}
                        >
                          {u.role === 'super_admin' || u.role === 'admin' ? (
                            <ShieldCheck size={12} aria-hidden="true" />
                          ) : (
                            <UserCog size={12} aria-hidden="true" />
                          )}
                          {ROLE_LABEL[u.role]}
                        </span>
                        {editingName ? (
                          <span className="flex flex-wrap items-center gap-1.5">
                            <input
                              type="text"
                              value={nameDraft}
                              onChange={(ev) => setNameDraft(ev.target.value)}
                              onKeyDown={(ev) => {
                                if (ev.key === 'Enter') void onSaveName(u);
                                if (ev.key === 'Escape') cancelEditName();
                              }}
                              autoFocus
                              disabled={savingName}
                              placeholder="First Last"
                              maxLength={200}
                              aria-label={`New name for ${u.name || 'account'}`}
                              className="h-8 w-56 rounded-md border border-brand-surface bg-brand-white px-2 text-sm font-semibold text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow disabled:opacity-60"
                            />
                            <button
                              type="button"
                              onClick={() => void onSaveName(u)}
                              disabled={savingName}
                              className="inline-flex h-8 items-center gap-1 rounded-md bg-brand-charcoal px-2 text-xs font-semibold text-brand-white hover:bg-brand-charcoal/90 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Check size={12} aria-hidden="true" />
                              {savingName ? 'Saving...' : 'Save name'}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditName}
                              disabled={savingName}
                              className="inline-flex h-8 items-center gap-1 rounded-md border border-brand-surface bg-brand-white px-2 text-xs font-semibold text-brand-charcoal hover:bg-brand-surface/40 disabled:opacity-50"
                            >
                              <X size={12} aria-hidden="true" />
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <span className="text-sm font-semibold text-brand-charcoal">
                            {u.name || '(no name)'}
                          </span>
                        )}
                        {u.unit_number ? (
                          <span className="text-sm text-brand-body-medium">
                            Unit {u.unit_number}
                          </span>
                        ) : null}
                        {u.must_change_password ? (
                          <span className="rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                            Must change password
                          </span>
                        ) : null}
                        {isSelf ? (
                          <span className="rounded bg-brand-surface px-2 py-0.5 text-xs text-brand-body-medium">
                            You
                          </span>
                        ) : null}
                      </div>
                      {editingEmail ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <input
                            type="email"
                            value={emailDraft}
                            onChange={(ev) => setEmailDraft(ev.target.value)}
                            onKeyDown={(ev) => {
                              if (ev.key === 'Enter') void onSaveEmail(u);
                              if (ev.key === 'Escape') cancelEditEmail();
                            }}
                            autoFocus
                            disabled={savingEmail}
                            placeholder="owner@example.com"
                            aria-label={`New email for ${u.name || 'account'}`}
                            className="h-10 w-full max-w-xs rounded-md border border-brand-surface bg-brand-white px-3 text-sm text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow disabled:opacity-60"
                          />
                          <button
                            type="button"
                            onClick={() => void onSaveEmail(u)}
                            disabled={savingEmail}
                            className="inline-flex h-10 items-center gap-2 rounded-md bg-brand-charcoal px-4 text-sm font-semibold text-brand-white hover:bg-brand-charcoal/90 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Check size={14} aria-hidden="true" />
                            {savingEmail ? 'Saving...' : 'Save email'}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditEmail}
                            disabled={savingEmail}
                            className="inline-flex h-10 items-center gap-2 rounded-md border border-brand-surface bg-brand-white px-3 text-sm font-semibold text-brand-charcoal hover:bg-brand-surface/40 disabled:opacity-50"
                          >
                            <X size={14} aria-hidden="true" />
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-brand-body-medium">
                          <span className="truncate">{u.email || 'No email on file'}</span>
                          {isPlaceholderEmail ? (
                            <span className="rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                              Placeholder
                            </span>
                          ) : null}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => startEditName(u)}
                        disabled={!allowed || anyActionInFlight || editingAnywhere}
                        title={
                          isSelf
                            ? 'Change your own display name in PocketBase, not here'
                            : !allowed
                              ? 'Only a super admin can change another super admin name'
                              : "Update this owner's display name (e.g. fix a surname-first entry, replace (no name), correct a typo)"
                        }
                        className="inline-flex h-10 items-center gap-2 rounded-md border border-brand-charcoal bg-brand-white px-4 text-sm font-semibold text-brand-charcoal hover:bg-brand-surface/40 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Pencil size={14} aria-hidden="true" />
                        Edit name
                      </button>
                      <button
                        type="button"
                        onClick={() => startEditEmail(u)}
                        disabled={!allowed || anyActionInFlight || editingAnywhere}
                        title={
                          isSelf
                            ? 'Change your own email in PocketBase, not here'
                            : !allowed
                              ? 'Only a super admin can change another super admin email'
                              : "Update this owner's email address (e.g. replace a placeholder condoNNN@test.com with the real address)"
                        }
                        className="inline-flex h-10 items-center gap-2 rounded-md border border-brand-charcoal bg-brand-white px-4 text-sm font-semibold text-brand-charcoal hover:bg-brand-surface/40 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Mail size={14} aria-hidden="true" />
                        Edit email
                      </button>
                      <button
                        type="button"
                        onClick={() => void onSendResetLink(u)}
                        disabled={!allowed || anyActionInFlight || editingAnywhere}
                        title={
                          isSelf
                            ? 'Use the change password page for your own account'
                            : !allowed
                              ? 'Only a super admin can send a reset link to another super admin'
                              : 'Send an email with a one-click reset link (recommended for owners who struggle with the temp password)'
                        }
                        className="inline-flex h-10 items-center gap-2 rounded-md bg-brand-yellow px-4 text-sm font-semibold text-brand-charcoal hover:bg-brand-yellow-deep disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <MailCheck size={14} aria-hidden="true" />
                        {sendingLink ? 'Sending...' : 'Send reset link'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void onReset(u)}
                        disabled={!allowed || anyActionInFlight || editingAnywhere}
                        title={
                          isSelf
                            ? 'Use the change password page for your own account'
                            : !allowed
                              ? 'Only a super admin can reset another super admin'
                              : 'Set the password to Welcome3873 and email a notification (use when walking the owner through by phone)'
                        }
                        className="inline-flex h-10 items-center gap-2 rounded-md border border-brand-surface bg-brand-white px-4 text-sm font-semibold text-brand-charcoal hover:bg-brand-surface/40 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <KeyRound size={14} aria-hidden="true" />
                        {resetting ? 'Resetting...' : 'Set temp password'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="mt-6 text-xs leading-relaxed text-brand-body-medium">
            <strong>Edit name</strong> updates the display name on the account. Use it to fix a
            surname-first entry, replace <code>(no name)</code>, or correct a typo. Email, password,
            and role are not touched.{' '}
            <strong>Edit email</strong> updates the address an owner signs in with and receives portal
            email at. Use it to replace a placeholder <code>condoNNN@test.com</code> with the real
            address once the board provides it; the owner can sign in with the new address right away
            and their password is not changed.{' '}
            <strong>Send reset link</strong> emails the owner a one-click link to pick a new password
            directly. Best for owners who struggle with typing the temporary password. Link is valid
            for 30 minutes.{' '}
            <strong>Set temp password</strong> sets the account to the shared default (Welcome3873)
            and emails a notification. Use when you plan to walk the owner through it by phone.
            Every action is recorded in the audit log.
          </p>
        </Container>
      </section>
    </>
  );
}
